import PriorityIcon from '@/components/linear/priority-icon';
import StatusIcon from '@/components/linear/status-icon';
import { openIssuePath } from '@/lib/linear-paths';
import type { Issue } from '@/types/issue';
import { router } from '@inertiajs/react';

interface Props {
  issues: Issue[];
  selectedId?: string | null;
  onSelect?: (issue: Issue) => void;
}

function startOfWeek(date: Date): Date {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export default function IssueTimeline({ issues, selectedId, onSelect }: Props) {
  const rangeStart = startOfWeek(addDays(new Date(), -28));
  const weeks = Array.from({ length: 8 }, (_, index) => addDays(rangeStart, index * 7));
  const rangeEnd = addDays(rangeStart, 56);
  const span = rangeEnd.getTime() - rangeStart.getTime();

  return (
    <div className="ln-timeline">
      <div className="ln-timeline-header">
        <div className="ln-timeline-label">Issue</div>
        <div className="ln-timeline-weeks">
          {weeks.map((week) => (
            <div key={week.toISOString()} className="ln-timeline-week">
              {week.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </div>
          ))}
        </div>
      </div>
      {issues.map((issue) => {
        const created = new Date(issue.createdAt);
        const end = new Date(issue.dueOn || issue.updatedAt || issue.createdAt);
        const barStart = Math.max(created.getTime(), rangeStart.getTime());
        const barEnd = Math.max(barStart + 86400000, Math.min(end.getTime(), rangeEnd.getTime()));
        const left = ((barStart - rangeStart.getTime()) / span) * 100;
        const width = ((barEnd - barStart) / span) * 100;
        const selected = selectedId === issue.id || selectedId === issue.identifier;

        return (
          <button
            key={issue.id}
            type="button"
            className="ln-timeline-row"
            data-selected={selected ? 'true' : 'false'}
            onClick={() => {
              if (onSelect) {
                onSelect(issue);
                return;
              }
              router.visit(openIssuePath(issue));
            }}
          >
            <div className="ln-timeline-label">
              <PriorityIcon priority={issue.priority} />
              <span className="ln-row-id">{issue.identifier}</span>
              <StatusIcon status={issue.status} />
              <span className="ln-row-title">{issue.title}</span>
            </div>
            <div className="ln-timeline-track">
              <div
                className="ln-timeline-bar"
                style={{ left: `${left}%`, width: `${Math.max(width, 1.5)}%` }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}
